import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PortalShell } from "@/components/portal/PortalShell";
import {
  createSignedUploadUrl,
  listProjectFiles,
  registerProjectFile,
} from "@/lib/uploads.functions";
import { listAnalyses, startAnalysis } from "@/lib/analysis-engine.functions";

export const Route = createFileRoute("/_authenticated/app/projects/$id/uploads")({
  component: ProjectUploadsPage,
});

function ProjectUploadsPage() {
  const { id: projectId } = Route.useParams();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const list = useServerFn(listProjectFiles);
  const listA = useServerFn(listAnalyses);
  const signUrl = useServerFn(createSignedUploadUrl);
  const register = useServerFn(registerProjectFile);
  const start = useServerFn(startAnalysis);

  const filesQ = useQuery({
    queryKey: ["project-files", projectId],
    queryFn: () => list({ data: { project_id: projectId } }),
  });
  const analysesQ = useQuery({
    queryKey: ["analyses", projectId],
    queryFn: () => listA({ data: { project_id: projectId } }),
  });

  const uploadMany = async (files: File[]) => {
    setErr(null);
    setUploading(true);
    try {
      for (const f of files) {
        const { bucket, path, token } = await signUrl({
          data: { project_id: projectId, bucket: "customer-uploads", filename: f.name },
        });
        const up = await supabase.storage.from(bucket).uploadToSignedUrl(path, token, f, {
          contentType: f.type || "application/octet-stream",
        });
        if (up.error) throw new Error(up.error.message);
        await register({
          data: {
            project_id: projectId,
            bucket,
            path,
            label: f.name,
            mime_type: f.type || "application/octet-stream",
            size_bytes: f.size,
          },
        });
      }
      await qc.invalidateQueries({ queryKey: ["project-files", projectId] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  const startMut = useMutation({
    mutationFn: () =>
      start({ data: { project_id: projectId, type: "documents", file_ids: Array.from(selected) } }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["analyses", projectId] });
    },
  });

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <PortalShell nav={[{ to: "/app/crm", label: "CRM" }, { to: "/app/portal", label: "Portal" }]}>
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Uploads & Analyse</h1>
            <p className="text-sm text-muted-foreground">Projekt: {projectId}</p>
          </div>
          <Link to="/app/crm/projects/$id" params={{ id: projectId }} className="text-sm underline">
            ← Zur Projektakte
          </Link>
        </div>

        <section className="rounded-md border p-6 mb-8">
          <h2 className="font-medium mb-3">Dateien hochladen</h2>
          <p className="text-sm text-muted-foreground mb-3">
            Dokumente (PDF, Office), Bilder oder Sprachaufnahmen. Max. 50 MB je Datei.
          </p>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json"
            onChange={(e) => {
              const list = e.target.files;
              if (list && list.length) {
                uploadMany(Array.from(list));
                if (fileInput.current) fileInput.current.value = "";
              }
            }}
            disabled={uploading}
            className="block w-full text-sm"
          />
          {uploading && <p className="mt-2 text-sm">Lade hoch…</p>}
          {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
        </section>

        <section className="rounded-md border p-6 mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-medium">Projekt-Dateien</h2>
            <button
              type="button"
              disabled={selected.size === 0 || startMut.isPending}
              onClick={() => startMut.mutate()}
              className="text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
            >
              {startMut.isPending ? "Analyse startet…" : `Analyse starten (${selected.size})`}
            </button>
          </div>
          {filesQ.isLoading && <p className="text-sm">Lade…</p>}
          {filesQ.data?.files?.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Dateien.</p>
          )}
          <ul className="divide-y">
            {filesQ.data?.files?.map((f: any) => (
              <li key={f.id} className="py-2 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selected.has(f.id)}
                  onChange={() => toggle(f.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{f.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {f.media_kind ?? "?"} · {f.classification ?? "—"} · Trust {f.trust_level ?? "—"} ·{" "}
                    {f.size_bytes ? `${Math.round(f.size_bytes / 1024)} KB` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-md border p-6">
          <h2 className="font-medium mb-3">Analyse-Historie</h2>
          {analysesQ.isLoading && <p className="text-sm">Lade…</p>}
          {analysesQ.data?.analyses?.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Analyse.</p>
          )}
          <ul className="divide-y">
            {analysesQ.data?.analyses?.map((a: any) => (
              <li key={a.id} className="py-2 text-sm flex items-center justify-between">
                <span>
                  <span className="font-mono">{a.analysis_no}</span> · {a.type} ·{" "}
                  <span className="text-muted-foreground">{a.status}</span> · Trust{" "}
                  {a.trust_level ?? "—"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleString("de-DE")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </PortalShell>
  );
}
